import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getEstimates, updateEstimate, deleteEstimate, createEstimate } from '../../services/estimate-service';
import { useAuth } from '../../contexts/AuthContext';
import { getAssignableUsers } from '../../services/user-service';
import { getJobOrderByEstimate } from '../../services/job-order-service';
import { generateQuotePDF, generateQuotePDFBlob } from '../../services/estimate-pdf';
import { generateJobOrderPDF } from '../../services/job-order-pdf';
import { fillContractPDF, buildFieldsFromContract } from '../../services/contract-pdf-fill';
import TrimOrderModal from '../../components/TrimOrderModal';
import OrderedWindowsShopOrderModal from '../../components/OrderedWindowsShopOrderModal';
import { useConfirm } from '../../components/ConfirmDialog';
import AppLayout from '../../components/AppLayout';
import Button from '../../components/ui/Button';
import { ArrowLeft, Pencil } from 'lucide-react';

// ── Palette — same warm, coherent family as the Estimates page.
const INK    = '#2B2620';
const WALNUT = '#8B4513';
const MOSS   = '#3B6B44';
const OCHRE  = '#A6752A';
const RUST   = '#A13D2D';
const STONE  = '#8C8275';
const PAPER  = '#FDFBF8';

const FONT_DISPLAY = "'Fraunces', Georgia, serif";
const FONT_SANS    = "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const FONT_MONO    = "'IBM Plex Mono', 'SFMono-Regular', Consolas, monospace";

const STATUS_CONFIG = {
  draft:    { label: 'Draft',    color: STONE, bg: '#F1EDE7' },
  sent:     { label: 'Sent',     color: OCHRE, bg: '#FBF1E0' },
  approved: { label: 'Approved', color: MOSS,  bg: '#EAF1EA' },
  archived: { label: 'Archived', color: STONE, bg: '#F5F3F0' },
};

/* ══════════════════════════════════════════════════════════════════════ */
/*  HELPERS                                                             */
/* ══════════════════════════════════════════════════════════════════════ */
function generateDefaultLabel() {
  const now = new Date();
  const month = now.toLocaleString('en-US', { month: 'short' });
  const day = now.getDate();
  const year = now.getFullYear();
  return `TWP Estimate – ${month} ${day}, ${year}`;
}

async function renderPdfToImages(pdfData, scale = 2) {
  if (!window.pdfjsLib) {
    await new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
    window.pdfjsLib.GlobalWorkerOptions.workerSrc =
      'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
  }
  const pdf = await window.pdfjsLib.getDocument({ data: pdfData }).promise;
  const images = [];
  for (let i = 1; i <= pdf.numPages; i++) {
    const page     = await pdf.getPage(i);
    const viewport = page.getViewport({ scale });
    const canvas   = document.createElement('canvas');
    canvas.width   = viewport.width;
    canvas.height  = viewport.height;
    const ctx      = canvas.getContext('2d');
    await page.render({ canvasContext: ctx, viewport }).promise;
    images.push(canvas.toDataURL('image/png'));
  }
  return images;
}


/* ══════════════════════════════════════════════════════════════════════ */
/*  DUPLICATE MODAL                                                     */
/* ══════════════════════════════════════════════════════════════════════ */
function DuplicateModal({ sourceEstimate, onConfirm, onCancel, loading }) {
  const defaultLabel = `Copy of ${sourceEstimate.label || sourceEstimate.estimateId || generateDefaultLabel()}`;
  const [label, setLabel] = useState(defaultLabel);
  const inputRef = useRef(null);

  useEffect(() => {
    setTimeout(() => inputRef.current?.select(), 80);
  }, []);

  function handleKeyDown(e) {
    if (e.key === 'Enter') onConfirm(label.trim() || defaultLabel);
    if (e.key === 'Escape') onCancel();
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 10000,
      background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(3px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
    }}>
      <div style={{
        background: 'white', borderRadius: '16px', padding: '28px 28px 24px',
        width: '100%', maxWidth: '440px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }}>
        <div style={{ fontSize: '18px', fontWeight: '800', color: '#1a1a1a', marginBottom: '6px' }}>
          Duplicate Estimate
        </div>
        <div style={{ fontSize: '13px', color: '#888', marginBottom: '20px' }}>
          All line items will be copied. Give this package a name so AJ can tell them apart.
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ fontSize: '11px', fontWeight: '700', color: '#aaa', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}>
            ESTIMATE NAME / PACKAGE LABEL
          </label>
          <input
            ref={inputRef}
            name="twp-duplicate-estimate-label"
            autoComplete="off"
            value={label}
            onChange={e => setLabel(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={defaultLabel}
            style={{
              width: '100%', padding: '10px 14px', borderRadius: '10px',
              border: '2px solid #e0d5cc', fontSize: '14px',
              fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
              transition: 'border-color 0.15s',
            }}
            onFocus={e => e.target.style.borderColor = WALNUT}
            onBlur={e => e.target.style.borderColor = '#e0d5cc'}
          />
          <div style={{ fontSize: '11px', color: '#bbb', marginTop: '5px' }}>
            e.g. &ldquo;Package 1 – TWP Windows&rdquo;, &ldquo;Terry Doors&rdquo;, &ldquo;Living Room Only&rdquo;
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={onCancel} disabled={loading} style={{
            flex: 1, padding: '11px', borderRadius: '10px',
            border: '1px solid #e0e0e0', background: 'white',
            color: '#666', fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit',
          }}>Cancel</button>
          <button onClick={() => onConfirm(label.trim() || defaultLabel)} disabled={loading} style={{
            flex: 2, padding: '11px', borderRadius: '10px',
            border: 'none', background: WALNUT,
            color: 'white', fontSize: '14px', fontWeight: '700',
            cursor: loading ? 'wait' : 'pointer', fontFamily: 'inherit',
          }}>
            {loading ? 'Duplicating…' : 'Duplicate & Edit →'}
          </button>
        </div>
      </div>
    </div>
  );
}


/* ══════════════════════════════════════════════════════════════════════ */
/*  PDF PREVIEW MODAL                                                   */
/* ══════════════════════════════════════════════════════════════════════ */
function PdfPreviewModal({ title, images, pdfBytes, fileName, onClose, loading: isLoading }) {
  const [zoom, setZoom] = React.useState(100);
  const isLandscape = title === 'Job Order' || title === 'Contract' ||
    (title || '').includes('Shop Order') || (title || '').includes('Trim Order');

  React.useEffect(() => { setZoom(isLandscape ? 80 : 100); }, [isLandscape]);

  function handleDownload() {
    if (!pdfBytes) return;
    const blob = pdfBytes instanceof Blob ? pdfBytes : new Blob([pdfBytes], { type: 'application/pdf' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = fileName || 'document.pdf';
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  }

  function zoomIn()    { setZoom(z => Math.min(z + 15, 200)); }
  function zoomOut()   { setZoom(z => Math.max(z - 15, 30)); }
  function fitWidth()  { setZoom(isLandscape ? 80 : 70); }
  function fitHeight() { setZoom(isLandscape ? 55 : 100); }
  function resetZoom() { setZoom(isLandscape ? 80 : 100); }

  const zoomBtnStyle = {
    width: '34px', height: '34px', background: 'rgba(255,255,255,0.12)',
    border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px',
    color: 'white', fontSize: '18px', fontWeight: '700',
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: 'inherit',
  };
  const fitBtnStyle = {
    padding: '5px 10px', background: 'rgba(255,255,255,0.12)',
    border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px',
    color: 'rgba(255,255,255,0.85)', fontSize: '10px', fontWeight: '600',
    cursor: 'pointer', fontFamily: 'inherit', letterSpacing: '0.3px',
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
      display: 'flex', flexDirection: 'column',
    }}>
      <div style={{
        background: 'white', padding: '12px 20px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        borderBottom: '1px solid #e8e8e8', flexShrink: 0,
      }}>
        <div style={{ fontSize: '16px', fontWeight: '700', color: '#1a1a1a' }}>
          {title} {fileName && <span style={{ fontSize: '12px', color: '#aaa', fontWeight: '400' }}>— {fileName}</span>}
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {pdfBytes && (
            <>
              <button onClick={() => {
                const blob = pdfBytes instanceof Blob ? pdfBytes : new Blob([pdfBytes], { type: 'application/pdf' });
                const url = URL.createObjectURL(blob);
                const iframe = document.createElement('iframe');
                Object.assign(iframe.style, { position: 'fixed', right: '0', bottom: '0', width: '0', height: '0', border: 'none' });
                iframe.src = url;
                document.body.appendChild(iframe);
                iframe.onload = () => {
                  setTimeout(() => {
                    iframe.contentWindow.print();
                    setTimeout(() => { document.body.removeChild(iframe); URL.revokeObjectURL(url); }, 1000);
                  }, 500);
                };
              }} style={{
                padding: '8px 16px', borderRadius: '8px', border: 'none',
                background: '#00695c', color: 'white', fontSize: '13px',
                fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit',
              }}>Print</button>
              <button onClick={handleDownload} style={{
                padding: '8px 16px', borderRadius: '8px', border: 'none',
                background: WALNUT, color: 'white', fontSize: '13px',
                fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit',
              }}>⬇ Download</button>
            </>
          )}
          <button onClick={onClose} style={{
            padding: '8px 16px', borderRadius: '8px',
            border: '1px solid #e0e0e0', background: 'white',
            color: '#666', fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit',
          }}>Close</button>
        </div>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', background: '#525659', padding: '16px', position: 'relative' }}>
        <div style={{ maxWidth: (zoom * 8) + 'px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '8px', transition: 'max-width 0.2s ease' }}>
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: 'rgba(255,255,255,0.6)' }}>
              <style>{`@keyframes hubspin{to{transform:rotate(360deg)}}`}</style>
              <div style={{ width: '32px', height: '32px', border: '3px solid rgba(255,255,255,0.2)', borderTop: '3px solid white', borderRadius: '50%', animation: 'hubspin 0.8s linear infinite', margin: '0 auto 12px' }} />
              Loading document...
            </div>
          ) : images && images.length > 0 ? (
            images.map((src, i) => (
              <img key={i} src={src} alt={`Page ${i + 1}`} style={{
                width: '100%', height: 'auto', borderRadius: '6px',
                boxShadow: '0 2px 12px rgba(0,0,0,0.12)', display: 'block',
              }} />
            ))
          ) : (
            <div style={{ textAlign: 'center', padding: '60px', color: 'rgba(255,255,255,0.5)' }}>No preview available</div>
          )}
        </div>
        {!isLoading && images && images.length > 0 && (
          <div style={{
            position: 'sticky', bottom: '16px', left: 0, right: 0,
            display: 'flex', justifyContent: 'center', zIndex: 5,
            pointerEvents: 'none', marginTop: '12px',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '6px 10px', background: 'rgba(30,30,30,0.85)',
              borderRadius: '12px', backdropFilter: 'blur(8px)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.4)', pointerEvents: 'auto',
            }}>
              <button onClick={zoomOut} style={zoomBtnStyle}>−</button>
              <div style={{ color: 'white', fontSize: '12px', fontWeight: '700', minWidth: '44px', textAlign: 'center', fontFamily: 'monospace' }}>{zoom}%</div>
              <button onClick={zoomIn} style={zoomBtnStyle}>+</button>
              <div style={{ width: '1px', height: '20px', background: 'rgba(255,255,255,0.2)', margin: '0 4px' }} />
              <button onClick={fitWidth} style={fitBtnStyle}>Width</button>
              <button onClick={fitHeight} style={fitBtnStyle}>Height</button>
              <button onClick={resetZoom} style={fitBtnStyle}>Reset</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// Strip undefined values — Firestore rejects them outright
function clean(obj) {
  if (Array.isArray(obj)) return obj.map(clean);
  if (obj && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj)
        .filter(([, v]) => v !== undefined)
        .map(([k, v]) => [k, clean(v)])
    );
  }
  return obj;
}

/* ══════════════════════════════════════════════════════════════════════ */
/*  DOCUMENTS SECTION (collapsible, per estimate)                       */
/* ══════════════════════════════════════════════════════════════════════ */
function DocumentsSection({ estimate }) {
  const [preview, setPreview]                         = useState(null);
  const [previewLoading, setPreviewLoading]           = useState(false);
  const [jobOrder, setJobOrder]                       = useState(undefined);
  const [showTrimOrder, setShowTrimOrder]             = useState(false);
  const [showOrderedShopOrder, setShowOrderedShopOrder] = useState(false);

  const contract    = estimate?.contract || {};
  const hasContract = !!contract.contractNumber;
  const isSigned    = contract.contractStatus === 'signed';
  const hasJobOrder = jobOrder !== undefined && jobOrder !== null;

  const hasOrderedItems = hasJobOrder && (jobOrder.windows || []).some(w => {
    if (w.lineType === 'twp_window') return false;
    if (w.lineType === 'adder' || w.type === 'adder') return false;
    return true;
  });

  // Auto-load job order on mount (always-expanded means we need it ready)
  useEffect(() => {
    if (!estimate?.id) return;
    if (jobOrder !== undefined) return;
    getJobOrderByEstimate(estimate.id)
      .then(jo => setJobOrder(jo || null))
      .catch(() => setJobOrder(null));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- load once per estimate; jobOrder guard is intentional
  }, [estimate?.id]);

  async function previewEstimate() {
    setPreviewLoading(true);
    setPreview({ title: 'Estimate', images: [], pdfBytes: null, fileName: '' });
    try {
      const contractNumber = jobOrder?.contractNumber || null;
      const blob = await generateQuotePDFBlob(estimate, {}, contractNumber);
      const uint8 = new Uint8Array(await blob.arrayBuffer());
      const images = await renderPdfToImages(uint8);
      setPreview({
        title: `Estimate — ${estimate.estimateId || ''}`,
        images, pdfBytes: blob,
        fileName: `Estimate_${estimate.customer?.name || 'Document'}.pdf`,
      });
    } catch (e) {
      console.error('Estimate preview error:', e);
      try { generateQuotePDF(estimate); } catch(e2) { console.error(e2); }
      setPreview(null);
    }
    setPreviewLoading(false);
  }

  async function previewJobOrder() {
    if (!jobOrder) return;
    setPreviewLoading(true);
    setPreview({ title: 'Job Order', images: [], pdfBytes: null, fileName: '' });
    try {
      const blob = await generateJobOrderPDF(jobOrder, true);
      const uint8 = new Uint8Array(await blob.arrayBuffer());
      const images = await renderPdfToImages(uint8);
      const safeName = (estimate.customer?.name || 'Customer').replace(/\s+/g, '_');
      setPreview({
        title: `Job Order — #${jobOrder.contractNumber || estimate.estimateId || ''}`,
        images, pdfBytes: blob,
        fileName: `JobOrder_${jobOrder.contractNumber || ''}_${safeName}.pdf`,
      });
    } catch (e) {
      console.error('Job order preview error:', e);
      try { generateJobOrderPDF(jobOrder, false); } catch(e2) { console.error(e2); }
      setPreview(null);
    }
    setPreviewLoading(false);
  }

  async function previewContract() {
    if (!hasContract) return;
    setPreviewLoading(true);
    setPreview({ title: 'Contract', images: [], pdfBytes: null, fileName: '' });
    try {
      const fields = buildFieldsFromContract(contract);
      const bytes = await fillContractPDF(
        fields,
        contract.repSignature || null,
        isSigned ? contract.signedBy?.signatureData : null,
      );
      const images = await renderPdfToImages(bytes);
      setPreview({
        title: `Contract #${contract.contractNumber || '—'}${isSigned ? ' (Signed)' : ' (Draft)'}`,
        images, pdfBytes: bytes,
        fileName: `Contract_${contract.contractNumber || ''}_${estimate.customer?.name || 'Document'}.pdf`,
      });
    } catch (e) {
      console.error('Contract preview error:', e);
      setPreview(null);
    }
    setPreviewLoading(false);
  }

  // Build the list of available documents
  const docs = [];

  // Estimate (always)
  docs.push({
    label: 'Estimate',
    subtitle: `${estimate.estimateId || '—'} - ${(estimate.lines || []).filter(l => l.type !== 'note').length} items - $${(estimate.grandTotal || 0).toFixed(2)}`,
    statusText: estimate.status === 'approved' ? 'Approved' : estimate.status === 'sent' ? 'Sent' : 'Draft',
    statusColor: STATUS_CONFIG[estimate.status]?.color || STONE,
    statusBg:    STATUS_CONFIG[estimate.status]?.bg    || '#F1EDE7',
    onView: previewEstimate,
    available: true,
  });

  // Contract
  docs.push({
    label: 'Contract',
    subtitle: hasContract
      ? `#${contract.contractNumber}${isSigned ? ' - Signed' : ' - Draft'}`
      : 'Not generated yet',
    statusText: hasContract ? (isSigned ? 'Signed' : 'Draft') : 'Pending',
    statusColor: hasContract ? (isSigned ? MOSS : STONE) : STONE,
    statusBg:    hasContract ? (isSigned ? '#EAF1EA' : '#F1EDE7') : '#F5F3F0',
    onView: hasContract ? previewContract : null,
    available: hasContract,
  });

  // Job Order
  docs.push({
    label: 'Job Order',
    subtitle: hasJobOrder
      ? `#${jobOrder.contractNumber || '—'} - ${jobOrder.status === 'approved' ? 'Approved' : 'Pending'}`
      : 'Not created yet',
    statusText: hasJobOrder ? (jobOrder.status === 'approved' ? 'Approved' : 'Pending') : 'Pending',
    statusColor: hasJobOrder ? (jobOrder.status === 'approved' ? MOSS : OCHRE) : STONE,
    statusBg:    hasJobOrder ? (jobOrder.status === 'approved' ? '#EAF1EA' : '#FBF1E0') : '#F5F3F0',
    onView: hasJobOrder ? previewJobOrder : null,
    available: hasJobOrder,
  });

  // Trim Order
  docs.push({
    label: 'Trim Order',
    subtitle: hasJobOrder ? 'Generate from job order' : 'Requires job order',
    statusText: hasJobOrder ? 'Available' : 'Pending',
    statusColor: hasJobOrder ? WALNUT : STONE,
    statusBg:    hasJobOrder ? '#F3E8DC' : '#F5F3F0',
    onView: hasJobOrder ? () => setShowTrimOrder(true) : null,
    available: hasJobOrder,
  });

  // Ordered Windows
  docs.push({
    label: 'Ordered Windows',
    subtitle: hasOrderedItems ? 'Shop order ready' : 'No ordered windows',
    statusText: hasOrderedItems ? 'Available' : 'N/A',
    statusColor: hasOrderedItems ? WALNUT : STONE,
    statusBg:    hasOrderedItems ? '#F3E8DC' : '#F5F3F0',
    onView: hasOrderedItems ? () => setShowOrderedShopOrder(true) : null,
    available: hasOrderedItems,
  });

  return (
    <>
      <div style={{
        background: '#fbf9f6',
        borderTop: '1px solid #f0ebe6',
        padding: '14px 20px 16px',
        borderRadius: '0 0 14px 14px',
      }}>
        <div style={{
          fontSize: '12px', fontWeight: 600, color: STONE,
          marginBottom: '10px', fontFamily: FONT_SANS,
        }}>Documents</div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '10px',
        }}>
          {docs.map((doc, i) => {
            const enabled = !!doc.onView;
            return (
              <button
                key={i}
                onClick={enabled ? doc.onView : undefined}
                disabled={!enabled}
                style={{
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'flex-start', gap: '6px',
                  background: 'white',
                  border: '1px solid #e8e2da',
                  borderRadius: '10px',
                  padding: '12px 14px',
                  textAlign: 'left',
                  cursor: enabled ? 'pointer' : 'not-allowed',
                  opacity: enabled ? 1 : 0.55,
                  fontFamily: 'inherit',
                  transition: 'all 120ms',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.02)',
                }}
                onMouseEnter={(e) => {
                  if (enabled) {
                    e.currentTarget.style.borderColor = WALNUT;
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(139,69,19,0.12)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e8e2da';
                  e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.02)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div style={{
                  display: 'flex', alignItems: 'center',
                  justifyContent: 'space-between', width: '100%',
                }}>
                  <div style={{
                    fontSize: '13px', fontWeight: '800', color: '#1a202c',
                  }}>{doc.label}</div>
                  <span style={{
                    fontSize: '9px', fontWeight: '700',
                    padding: '3px 8px', borderRadius: '20px',
                    background: doc.statusBg, color: doc.statusColor,
                    letterSpacing: '0.04em', textTransform: 'uppercase',
                  }}>{doc.statusText}</span>
                </div>
                <div style={{
                  fontSize: '11px', color: '#6b7280',
                  fontWeight: '500',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  width: '100%',
                }}>{doc.subtitle}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Preview modal */}
      {preview && (
        <PdfPreviewModal
          title={preview.title}
          images={preview.images}
          pdfBytes={preview.pdfBytes}
          fileName={preview.fileName}
          loading={previewLoading}
          onClose={() => setPreview(null)}
        />
      )}

      {/* Trim Order modal */}
      {showTrimOrder && jobOrder && (
        <TrimOrderModal
          jobOrder={jobOrder}
          estimate={estimate}
          onClose={() => setShowTrimOrder(false)}
        />
      )}

      {/* Ordered Windows shop order modal */}
      {showOrderedShopOrder && jobOrder && (
        <OrderedWindowsShopOrderModal
          jobOrder={jobOrder}
          onClose={() => setShowOrderedShopOrder(false)}
        />
      )}
    </>
  );
}


/* ══════════════════════════════════════════════════════════════════════ */
/*  INLINE LABEL EDITOR                                                 */
/* ══════════════════════════════════════════════════════════════════════ */
function InlineLabel({ estimate, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal]         = useState(estimate.label || '');
  const inputRef              = useRef(null);

  useEffect(() => { setVal(estimate.label || ''); }, [estimate.label]);

  function startEdit() { setEditing(true); setTimeout(() => inputRef.current?.select(), 50); }

  function commit() {
    setEditing(false);
    const trimmed = val.trim();
    if (trimmed !== (estimate.label || '')) {
      onSave(trimmed || ''); // empty = fall back to auto label
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') commit();
    if (e.key === 'Escape') { setVal(estimate.label || ''); setEditing(false); }
  }

  const displayLabel = estimate.label || estimate.estimateId || generateDefaultLabel();

  if (editing) {
    return (
      <input
        ref={inputRef}
        name={`twp-hub-estimate-label-${estimate.id}`}
        autoComplete="off"
        value={val}
        onChange={e => setVal(e.target.value)}
        onBlur={commit}
        onKeyDown={handleKeyDown}
        placeholder={displayLabel}
        style={{
          fontSize: '15px', fontWeight: '700', color: INK,
          border: 'none', borderBottom: `2px solid ${WALNUT}`,
          outline: 'none', background: 'transparent',
          fontFamily: FONT_SANS, padding: '0 2px', width: '220px',
        }}
      />
    );
  }

  return (
    <span
      onClick={startEdit}
      title="Click to rename"
      style={{
        fontSize: '15px', fontWeight: '700', color: INK,
        cursor: 'text', display: 'inline-flex', alignItems: 'center', gap: '6px',
        fontFamily: FONT_SANS,
      }}
    >
      {displayLabel}
      <Pencil size={12} strokeWidth={2} color="#c9c0b6" />
    </span>
  );
}


/* ══════════════════════════════════════════════════════════════════════ */
/*  ESTIMATE CARD                                                       */
/* ══════════════════════════════════════════════════════════════════════ */
function ActionMenu({ est, onStatusChange, onDelete, onApprove, onDuplicate, onReassign, canReassign, navigate, isApproved }) {
  const [open, setOpen] = useState(false);
  const wrapRef = React.useRef(null);

  useEffect(() => {
    function onDoc(e) { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); }
    if (open) document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const items = [];
  if (canReassign) {
    items.push({ label: 'Reassign to…', onClick: () => { setOpen(false); onReassign(est); } });
  }
  items.push({ label: 'Print',     onClick: () => { setOpen(false); generateQuotePDF(est); } });
  items.push({ label: 'Duplicate', onClick: () => { setOpen(false); onDuplicate(est); } });
  if (!isApproved) {
    items.push({ label: 'Approve', onClick: () => { setOpen(false); onApprove(est); } });
  } else {
    items.push({ label: 'Open Job Order', onClick: () => { setOpen(false); navigate('/estimates/job-order/' + est.id); } });
  }
  items.push({ label: 'Mark Draft',    onClick: () => { setOpen(false); onStatusChange(est.id, 'draft'); } });
  items.push({ label: 'Mark Sent',     onClick: () => { setOpen(false); onStatusChange(est.id, 'sent'); } });
  items.push({ label: 'Delete', danger: true, onClick: () => { setOpen(false); onDelete(est.id); } });

  return (
    <div ref={wrapRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        style={{
          padding: '6px 10px', background: 'white',
          border: '1px solid #e0d5cc', borderRadius: '7px',
          color: '#4a5568', fontSize: '14px', fontWeight: '700',
          cursor: 'pointer', fontFamily: 'inherit',
          lineHeight: 1, letterSpacing: '0.1em',
        }}
        title="More actions"
      >...</button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', right: 0,
          background: 'white', border: '1px solid #e8e2da',
          borderRadius: '10px', padding: '6px',
          minWidth: '180px', zIndex: 9999,
          boxShadow: '0 12px 32px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)',
        }}>
          {items.map((it, i) => (
            <button
              key={i}
              onClick={(e) => { e.stopPropagation(); it.onClick(); }}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                padding: '8px 12px', background: 'transparent',
                border: 'none', borderRadius: '6px',
                color: it.danger ? RUST : '#2d3748',
                fontSize: '13px', fontWeight: '600',
                cursor: 'pointer', fontFamily: FONT_SANS,
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = it.danger ? '#FBEBE8' : '#faf6f1'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >{it.label}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function EstimateCard({ est, onStatusChange, onDelete, onApprove, onDuplicate, onReassign, onLabelSave, canReassign, navigate, formatDate }) {
  const st         = STATUS_CONFIG[est.status] || STATUS_CONFIG.draft;
  const lineCount  = (est.lines || []).filter(l => l.type !== 'note').length;
  const isApproved = est.status === 'approved';

  return (
    <div style={{
      background: PAPER, borderRadius: '14px',
      border: '1px solid #e8e2da',
      boxShadow: isApproved
        ? `0 0 0 2px ${MOSS}, 0 4px 12px rgba(0,0,0,0.05)`
        : '0 1px 3px rgba(0,0,0,0.04)',
      marginBottom: '14px',
      transition: 'box-shadow 150ms',
      position: 'relative',
      fontFamily: FONT_SANS,
    }}>
        {/* Approved label strip */}
        {isApproved && (
          <div style={{
            background: MOSS, color: 'white',
            fontSize: '11px', fontWeight: '600',
            padding: '5px 18px',
            borderRadius: '14px 14px 0 0',
          }}>
            Approved Estimate
          </div>
        )}

        {/* Column-aligned grid row */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '170px 1fr 110px 70px 100px 130px 110px 50px',
          gap: '14px', alignItems: 'center',
          padding: '18px 22px',
        }}>
          {/* Estimate ID */}
          <div style={{
            fontSize: '11px', color: STONE,
            fontFamily: FONT_MONO, fontWeight: '600',
          }}>{est.estimateId || '—'}</div>

          {/* Label (inline editable) */}
          <div style={{ minWidth: 0, overflow: 'hidden' }}>
            <InlineLabel estimate={est} onSave={label => onLabelSave(est.id, label)} />
          </div>

          {/* Date */}
          <div style={{ fontSize: '12px', color: STONE, fontWeight: '500' }}>
            {formatDate(est.createdAt)}
          </div>

          {/* Items */}
          <div style={{ fontSize: '12px', color: STONE, fontWeight: '600', textAlign: 'right' }}>
            {lineCount}
          </div>

          {/* Status */}
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: st.color, flexShrink: 0 }} />
            <span style={{ fontSize: '12px', fontWeight: 600, color: st.color }}>{st.label}</span>
          </span>

          {/* Amount */}
          <div style={{
            fontFamily: FONT_DISPLAY, fontSize: '17px', fontWeight: 600,
            color: INK, textAlign: 'right', whiteSpace: 'nowrap',
          }}>${(est.grandTotal || 0).toFixed(2)}</div>

          {/* Edit button (visible) */}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button onClick={() => navigate('/estimates/edit/' + est.id)} style={{
              padding: '7px 16px', background: WALNUT,
              border: 'none', borderRadius: '7px',
              color: 'white', fontSize: '12px', fontWeight: '700',
              cursor: 'pointer', fontFamily: FONT_SANS,
            }}>Edit</button>
          </div>

          {/* More menu */}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <ActionMenu
              est={est}
              onStatusChange={onStatusChange}
              onDelete={onDelete}
              onApprove={onApprove}
              onDuplicate={onDuplicate}
              onReassign={onReassign}
              canReassign={canReassign}
              navigate={navigate}
              isApproved={isApproved}
            />
          </div>
        </div>

      {/* Documents - always expanded, integrated into card */}
      <DocumentsSection estimate={est} />
    </div>
  );
}



/* ══════════════════════════════════════════════════════════════════════ */
/*  REASSIGN MODAL                                                       */
/* ══════════════════════════════════════════════════════════════════════ */
function ReassignModal({ estimate, assignees, onConfirm, onCancel, loading }) {
  const [selectedUid, setSelectedUid] = useState(estimate?.createdByUid || '');

  if (!estimate) return null;

  const currentOwnerName = estimate.createdByName || 'Unassigned';
  const newOwner = assignees.find(u => u.uid === selectedUid);
  const willChange = selectedUid && selectedUid !== (estimate.createdByUid || '');

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 10000, padding: '20px',
    }}
    onClick={onCancel}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'white', borderRadius: '14px',
        width: '100%', maxWidth: '460px',
        padding: '24px', boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }}>
        <div style={{ fontSize: '18px', fontWeight: '800', color: '#1a202c', marginBottom: '6px' }}>
          Reassign Estimate
        </div>
        <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '18px' }}>
          {estimate.estimateId || 'Estimate'} for {estimate.customer?.name || 'Customer'}
        </div>

        <div style={{
          background: '#fbf9f6', border: '1px solid #e8e2da',
          borderRadius: '10px', padding: '12px 14px', marginBottom: '14px',
        }}>
          <div style={{ fontSize: '10px', fontWeight: '700', letterSpacing: '0.06em', color: '#9ca3af', textTransform: 'uppercase', marginBottom: '4px' }}>Currently Assigned To</div>
          <div style={{ fontSize: '14px', fontWeight: '700', color: '#2d3748' }}>{currentOwnerName}</div>
        </div>

        <div style={{ marginBottom: '6px' }}>
          <div style={{ fontSize: '10px', fontWeight: '700', letterSpacing: '0.06em', color: '#9ca3af', textTransform: 'uppercase', marginBottom: '6px' }}>Reassign To</div>
          <select
            value={selectedUid}
            onChange={e => setSelectedUid(e.target.value)}
            style={{
              width: '100%', padding: '10px 12px',
              border: '1px solid #e0d5cc', borderRadius: '8px',
              background: 'white', color: '#2d3748',
              fontSize: '14px', fontWeight: '600',
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            <option value="">— Select user —</option>
            {assignees.map(u => (
              <option key={u.uid} value={u.uid}>
                {u.displayName} {u.role === 'officeManager' ? '(Office Manager)' : u.role === 'sales' ? '(Sales)' : ''}
              </option>
            ))}
          </select>
        </div>

        {willChange && (
          <div style={{
            marginTop: '14px', padding: '10px 12px',
            background: '#fef3c7', borderRadius: '8px',
            fontSize: '12px', color: '#92400e', fontWeight: '600',
          }}>
            The PDF &ldquo;Prepared By&rdquo; will update to {newOwner?.displayName || 'the new owner'}.
            They will see this estimate on their dashboard.
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'flex-end' }}>
          <button onClick={onCancel} disabled={loading} style={{
            padding: '9px 18px', background: 'white',
            border: '1px solid #e0d5cc', borderRadius: '8px',
            color: '#4a5568', fontSize: '13px', fontWeight: '700',
            cursor: 'pointer', fontFamily: 'inherit',
          }}>Cancel</button>
          <button
            onClick={() => onConfirm(selectedUid)}
            disabled={loading || !willChange}
            style={{
              padding: '9px 18px', background: willChange ? WALNUT : '#d6cfc8',
              border: 'none', borderRadius: '8px',
              color: 'white', fontSize: '13px', fontWeight: '700',
              cursor: willChange ? 'pointer' : 'not-allowed', fontFamily: 'inherit',
              opacity: loading ? 0.6 : 1,
            }}
          >{loading ? 'Reassigning…' : 'Reassign'}</button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════ */
/*  MAIN PAGE                                                           */
/* ══════════════════════════════════════════════════════════════════════ */
export default function CustomerHubPage() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const { customerName } = useParams();
  const decodedName = decodeURIComponent(customerName || '');

  const [estimates, setEstimates]         = useState([]);
  const { userProfile, currentUser } = useAuth();
  const [loading, setLoading]             = useState(true);
  const [duplicateSource, setDuplicateSource] = useState(null);
  const [reassignSource, setReassignSource]   = useState(null);
  const [reassigning, setReassigning]         = useState(false);
  const [assignees, setAssignees]             = useState([]);
  const [duplicating, setDuplicating]     = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [decodedName]);

  // Load assignees once for the Reassign modal — only if user can reassign
  const canReassign = userProfile?.role === 'officeManager' || userProfile?.role === 'admin' || userProfile?.role === 'manager';
  useEffect(() => {
    if (!canReassign) return;
    getAssignableUsers().then(setAssignees).catch(e => console.error('Load assignees failed:', e));
  }, [canReassign]);

  async function handleReassignConfirm(newUid) {
    if (!reassignSource || !newUid) return;
    const newOwner = assignees.find(u => u.uid === newUid);
    if (!newOwner) return;
    setReassigning(true);
    try {
      await updateEstimate(reassignSource.id, {
        createdByUid:   newOwner.uid,
        createdByName:  newOwner.displayName,
        createdByPhone: newOwner.displayPhone,
        createdBy:      newOwner.email,
      });
      setReassignSource(null);
      load();
    } catch (e) {
      console.error('Reassign failed:', e);
      alert('Reassignment failed. Please try again.');
    }
    setReassigning(false);
  }

  async function load() {
    setLoading(true);
    const all = await getEstimates();
    // Salespersons only see their own estimates for this customer.
    // Office manager / admin sees all estimates for this customer.
    const isSales = userProfile?.role === 'sales';
    const uid     = currentUser?.uid || null;
    const filtered = all
      .filter(e => (e.customer?.name || '') === decodedName)
      .filter(e => !isSales || (e.createdByUid && e.createdByUid === uid));
    setEstimates(filtered);
    setLoading(false);
  }

  // Approve: just mark this one approved, leave all others as-is
  async function handleApprove(est) {
    if (!(await confirm({ title: 'Mark this estimate as Approved?', message: `All other estimates for ${decodedName} will stay visible — nothing will be archived. After approving, you'll be taken straight to the Job Order page.`, confirmText: 'Approve' }))) return;
    await updateEstimate(est.id, { status: 'approved', approvedAt: new Date() });
    // Approval = "I'm starting work on this now" — jump directly to Job Order creation.
    navigate(`/estimates/job-order/${est.id}`);
  }

  async function handleStatusChange(id, newStatus) {
    await updateEstimate(id, { status: newStatus });
    load();
  }

  async function handleDelete(id) {
    if (!(await confirm({ title: 'Delete this estimate?', message: 'This cannot be undone.', tone: 'danger', confirmText: 'Delete' }))) return;
    await deleteEstimate(id);
    load();
  }

  async function handleLabelSave(id, label) {
    await updateEstimate(id, { label });
    load();
  }

  // Duplicate: show naming modal → create new estimate → navigate to edit
  async function handleDuplicateConfirm(label) {
    if (!duplicateSource) return;
    setDuplicating(true);
    try {
      const src = duplicateSource;


      const newData = clean({
        customer:       src.customer || {},
        lines:          (src.lines || []).map(l => ({ ...l })),
        grandTotal:     src.grandTotal  || 0,
        subtotal:       src.subtotal    || 0,
        taxTotal:       src.taxTotal    || 0,
        taxAmount:      src.taxAmount   || 0,
        taxEnabled:     src.taxEnabled  || false,
        taxRate:        src.taxRate,
        notes:          src.notes       || '',
        multiplier:     src.multiplier, // cleaned by clean() if undefined
        status:         'draft',
        label:          label,
        duplicatedFrom: src.id || src.estimateId || null,
      });

      const result = await createEstimate(newData);
      // createEstimate may return a doc ref { id } or a plain string — handle both
      const newId = typeof result === 'string' ? result : (result?.id ?? result);
      setDuplicateSource(null);
      setDuplicating(false);
      navigate(`/estimates/edit/${newId}`);
    } catch (e) {
      console.error('Duplicate failed:', e);
      setDuplicating(false);
    }
  }

  const formatDate = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Sort: approved first, then by date desc
  const sorted = [...estimates].sort((a, b) => {
    if (a.status === 'approved' && b.status !== 'approved') return -1;
    if (b.status === 'approved' && a.status !== 'approved') return 1;
    const aDate = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
    const bDate = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
    return bDate - aDate;
  });

  const customer = estimates[0]?.customer || {};
  const approved = estimates.find(e => e.status === 'approved');
  const activeCount = estimates.filter(e => e.status !== 'archived').length;

  const breadcrumb = `Dashboard › Estimates › ${decodedName}`;

  return (
    <AppLayout
      title={decodedName}
      breadcrumb={breadcrumb}
      topBarActions={
        <Button size="small" onClick={() => {
          const c = estimates.length > 0 ? estimates[0].customer : {};
          navigate('/estimates/new', { state: { prefillCustomer: { ...c, name: decodedName } } });
        }}>+ New Estimate</Button>
      }
    >
    <div style={{ fontFamily: FONT_SANS }}>

      {/* Back to Estimates — explicit affordance for navigating back up a
          level; Customer Hub isn't a top-level Sidebar item, so this can't
          rely on Sidebar nav alone. */}
      <button
        onClick={() => navigate('/estimates')}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '5px',
          background: 'none', border: 'none', color: STONE,
          fontSize: '12px', fontWeight: '600', cursor: 'pointer',
          fontFamily: FONT_SANS, padding: '4px 0', marginBottom: '10px',
        }}
        onMouseEnter={e => e.currentTarget.style.color = WALNUT}
        onMouseLeave={e => e.currentTarget.style.color = STONE}
      >
        <ArrowLeft size={13} strokeWidth={2} /> Back to Estimates
      </button>

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
        <span style={{ fontSize: '12px', color: STONE }}>Customer estimate hub</span>
        <span style={{ width: '3px', height: '3px', borderRadius: '50%', background: '#d8d0c6' }} />
        <span style={{ fontSize: '12px', color: STONE }}>{activeCount} estimate{activeCount !== 1 ? 's' : ''}</span>
        {approved && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '10px', fontWeight: 600, color: MOSS, background: '#EAF1EA', padding: '2px 9px', borderRadius: '10px' }}>
            <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: MOSS }} /> Approved
          </span>
        )}
      </div>

        {/* Customer profile + KPIs + pipeline */}
        {customer.name && (() => {
          const totalQuoted   = sorted.reduce((s, e) => s + (e.grandTotal || 0), 0);
          const approvedSum   = sorted.filter(e => e.status === 'approved').reduce((s, e) => s + (e.grandTotal || 0), 0);
          const sentSum       = sorted.filter(e => e.status === 'sent').reduce((s, e) => s + (e.grandTotal || 0), 0);
          const draftSum      = sorted.filter(e => !e.status || e.status === 'draft').reduce((s, e) => s + (e.grandTotal || 0), 0);
          const pipelineTotal = approvedSum + sentSum + draftSum;
          const totalItems    = sorted.reduce((s, e) => s + ((e.lines || []).filter(l => l.type !== 'note').length), 0);
          const totalEst      = sorted.length;

          // First contact + last activity (from createdAt timestamps)
          const dates = sorted.map(e => {
            const d = e.createdAt?.toDate ? e.createdAt.toDate() : new Date(e.createdAt || 0);
            return d.getTime();
          }).filter(t => t > 0);
          const firstContact = dates.length ? new Date(Math.min(...dates)) : null;
          const lastActivity = dates.length ? new Date(Math.max(...dates)) : null;
          const fmtShort = (d) => d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

          const fmtMoneyShort = (n) => {
            const v = Math.round(n || 0);
            if (v >= 1000000) return '$' + (v / 1000000).toFixed(v >= 10000000 ? 0 : 1) + 'M';
            if (v >= 1000)    return '$' + (v / 1000).toFixed(v >= 10000 ? 0 : 1) + 'k';
            return '$' + v.toLocaleString('en-US');
          };

          const pipelineSegments = [
            { label: 'Approved', value: approvedSum, dot: MOSS },
            { label: 'Sent',     value: sentSum,     dot: OCHRE },
            { label: 'Draft',    value: draftSum,    dot: STONE },
          ];

          return (
            <div style={{ marginBottom: '24px' }}>

              {/* Top row: profile (left) + hero stat + strip (right) */}
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>

                {/* Profile card */}
                <div style={{
                  flex: '1 1 300px',
                  background: PAPER, border: '1px solid #e8e2da',
                  borderRadius: '14px', padding: '22px 24px',
                  position: 'relative', overflow: 'hidden',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '18px' }}>
                    <div style={{
                      width: '54px', height: '54px', borderRadius: '50%',
                      background: WALNUT, color: 'white',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontFamily: FONT_DISPLAY, fontSize: '20px', fontWeight: 600, flexShrink: 0,
                    }}>
                      {decodedName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: '700', fontSize: '17px', color: INK }}>{decodedName}</div>
                      <div style={{ fontSize: '12px', color: STONE, marginTop: '3px' }}>
                        {[customer.city, customer.state].filter(Boolean).join(', ') || 'No location'}
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px', paddingTop: '14px', borderTop: '1px solid #f1ede7' }}>
                    {customer.phone && (
                      <div>
                        <div style={{ fontSize: '11px', color: STONE, fontWeight: 600 }}>Phone</div>
                        <div style={{ fontSize: '13px', color: INK, marginTop: '3px', fontWeight: '600' }}>{customer.phone}</div>
                      </div>
                    )}
                    {customer.email && (
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: '11px', color: STONE, fontWeight: 600 }}>Email</div>
                        <div style={{ fontSize: '13px', color: INK, marginTop: '3px', fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{customer.email}</div>
                      </div>
                    )}
                    {customer.address && (
                      <div style={{ gridColumn: '1 / -1' }}>
                        <div style={{ fontSize: '11px', color: STONE, fontWeight: 600 }}>Address</div>
                        <div style={{ fontSize: '13px', color: INK, marginTop: '3px', fontWeight: '600' }}>{customer.address}</div>
                      </div>
                    )}
                    <div>
                      <div style={{ fontSize: '11px', color: STONE, fontWeight: 600 }}>First contact</div>
                      <div style={{ fontSize: '13px', color: INK, marginTop: '3px', fontWeight: '600' }}>{fmtShort(firstContact)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '11px', color: STONE, fontWeight: 600 }}>Last activity</div>
                      <div style={{ fontSize: '13px', color: INK, marginTop: '3px', fontWeight: '600' }}>{fmtShort(lastActivity)}</div>
                    </div>
                  </div>
                </div>

                {/* Hero stat (Approved $, with embedded pipeline) */}
                <div style={{
                  background: WALNUT, borderRadius: '16px', padding: '26px 28px',
                  flex: '0 0 260px', display: 'flex', flexDirection: 'column',
                  justifyContent: 'space-between', color: 'white',
                }}>
                  <div style={{ fontSize: '12px', fontWeight: 600, color: 'rgba(255,255,255,0.72)' }}>Approved</div>
                  <div style={{ fontFamily: FONT_DISPLAY, fontSize: '38px', fontWeight: 600, lineHeight: 1, marginTop: '10px', letterSpacing: '-0.01em' }}>
                    {fmtMoneyShort(approvedSum)}
                  </div>
                  {pipelineTotal > 0 && (
                    <div style={{ marginTop: '18px' }}>
                      <div style={{ display: 'flex', borderRadius: '5px', overflow: 'hidden', height: '6px', gap: '2px', marginBottom: '9px' }}>
                        {pipelineSegments.filter(s => s.value > 0).map(s => (
                          <div key={s.label} style={{ flex: s.value / pipelineTotal, background: s.dot, minWidth: '3px' }} />
                        ))}
                      </div>
                      <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.7)' }}>{fmtMoneyShort(pipelineTotal)} total pipeline</div>
                    </div>
                  )}
                </div>

                {/* Supporting stat strip */}
                <div style={{ flex: '1 1 220px', background: PAPER, border: '1px solid #EAE2D8', borderRadius: '16px', display: 'flex', flexWrap: 'wrap' }}>
                  {[
                    { label: 'Estimates',    value: totalEst },
                    { label: 'Total quoted', value: fmtMoneyShort(totalQuoted) },
                    { label: 'Items',        value: totalItems },
                  ].map((it, i) => (
                    <div key={it.label} style={{ flex: '1 1 90px', padding: '18px 16px', borderLeft: i > 0 ? '1px solid #EAE2D8' : 'none' }}>
                      <div style={{ fontSize: '11px', fontWeight: 600, color: STONE }}>{it.label}</div>
                      <div style={{ fontFamily: FONT_DISPLAY, fontSize: '22px', fontWeight: 600, color: INK, marginTop: '4px' }}>{it.value}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })()}

        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: STONE, fontSize: '13px' }}>Loading…</div>
        ) : sorted.length === 0 ? (
          <div style={{
            background: PAPER, borderRadius: '14px', padding: '40px',
            textAlign: 'center', boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
            border: '1px solid #e8e3dd',
          }}>
            <div style={{ color: STONE, marginBottom: '16px', fontSize: '13px' }}>No estimates for this customer yet</div>
            <button
              onClick={() => navigate('/estimates/new', { state: { prefillCustomer: { ...customer, name: customer.name || decodedName } } })}
              style={{
                background: WALNUT, color: 'white', border: 'none', borderRadius: '8px',
                padding: '10px 20px', fontSize: '13px', fontWeight: '700',
                cursor: 'pointer', fontFamily: FONT_SANS,
              }}
            >+ Create First Estimate</button>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', paddingLeft: '4px' }}>
              <span style={{ fontSize: '13px', fontWeight: 600, color: INK }}>Estimates timeline</span>
              <span style={{ width: '3px', height: '3px', borderRadius: '50%', background: '#d8d0c6' }} />
              <span style={{ fontSize: '12px', color: STONE }}>{sorted.length}</span>
              {approved && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '10px', fontWeight: 600, color: MOSS, background: '#EAF1EA', padding: '2px 9px', borderRadius: '10px' }}>
                  <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: MOSS }} /> 1 Approved
                </span>
              )}
            </div>

            {/* Column header strip */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '170px 1fr 110px 70px 100px 130px 110px 50px',
              gap: '14px',
              padding: '8px 20px',
              fontSize: '11px', fontWeight: 600,
              color: STONE,
              marginBottom: '4px',
            }}>
              <div>Estimate ID</div>
              <div>Label</div>
              <div>Date</div>
              <div style={{ textAlign: 'right' }}>Items</div>
              <div>Status</div>
              <div style={{ textAlign: 'right' }}>Amount</div>
              <div style={{ textAlign: 'right' }}></div>
              <div></div>
            </div>
            {sorted.map(est => (
              <EstimateCard
                key={est.id}
                est={est}
                onStatusChange={handleStatusChange}
                onDelete={handleDelete}
                onApprove={handleApprove}
                onDuplicate={setDuplicateSource}
                onReassign={setReassignSource}
                canReassign={canReassign}
                onLabelSave={handleLabelSave}
                navigate={navigate}
                formatDate={formatDate}
              />
            ))}
          </>
        )}
      </div>

      {/* Duplicate naming modal */}
      {duplicateSource && (
        <DuplicateModal
          sourceEstimate={duplicateSource}
          loading={duplicating}
          onConfirm={handleDuplicateConfirm}
          onCancel={() => { if (!duplicating) setDuplicateSource(null); }}
        />
      )}

      {/* Reassign modal */}
      {reassignSource && (
        <ReassignModal
          estimate={reassignSource}
          assignees={assignees}
          loading={reassigning}
          onConfirm={handleReassignConfirm}
          onCancel={() => { if (!reassigning) setReassignSource(null); }}
        />
      )}
    </AppLayout>
  );
}